import { Event, EventSystem, GameState } from './types';
import { StateManager } from './types';

/**
 * 解码可能编码的内容
 * @param content - 可能编码的内容
 * @returns 解码后的内容
 */
function decodeContent(content: string): string {
  if (typeof content !== 'string') {
    return content;
  }
  
  // 检查是否有编码标记
  if (content.startsWith('ENCODED_JSON_BASE64:')) {
    try {
      const encodedPart = content.substring('ENCODED_JSON_BASE64:'.length);
      // 使用更安全的UTF-8解码方式
      const binaryString = atob(encodedPart);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new TextDecoder('utf-8').decode(bytes);
    } catch (error) {
      console.error('解码失败:', error);
      return content;
    }
  }
  
  // 如果没有编码标记，直接返回原内容
  return content;
}

/**
 * 事件索引文件结构
 */
interface EventIndexEntry {
  path: string;
  type: string;
  category: string;
  description: string;
}

interface EventIndex {
  version: string;
  lastUpdated: string;
  description: string;
  eventFiles: EventIndexEntry[];
}

/**
 * 事件系统实现
 * 负责加载、管理和随机选择事件
 */
export class GameEventSystem implements EventSystem {
  private allEvents: Event[] = []; // 修改：存储所有事件
  private randomEvents: Event[] = []; // 新增：仅存储随机事件
  private eventMap: Map<string, Event> = new Map();
  private stateManager: StateManager; // 添加 StateManager 引用

  /**
   * 构造函数
   * @param stateManager 状态管理器实例
   */
  constructor(stateManager: StateManager) { // 修改构造函数签名
    this.stateManager = stateManager;
  }

  /**
   * 从索引文件加载事件路径
   * @returns 事件文件路径数组
   */
  private async loadEventIndex(): Promise<string[]> {
    try {
      const response = await fetch('/events/event-index.json');
      if (!response.ok) {
        throw new Error(`Failed to load event index: ${response.status}`);
      }
      
      // 获取响应文本并解码
      const responseText = await response.text();
      const decodedText = decodeContent(responseText);
      const eventIndex: EventIndex = JSON.parse(decodedText);
      
      console.log(`Loaded event index version ${eventIndex.version}, updated: ${eventIndex.lastUpdated}`);
      
      return eventIndex.eventFiles.map(entry => entry.path);
    } catch (error) {
      console.warn('Failed to load event index, falling back to hardcoded paths:', error);
      return this.getFallbackEventPaths();
    }
  }

  /**
   * 获取备用的硬编码事件路径（仅用于紧急情况）
   * 当索引文件完全无法加载时使用的最小事件集
   * @returns 事件文件路径数组
   */
  private getFallbackEventPaths(): string[] {
    console.warn('Using fallback event paths - event index file failed to load');
    return [
        // 基础必需事件
        '/events/common/nothing_happened.json',
        '/events/random/traffic/traffic_jam.json',
        '/events/random/weather/rainy_day.json',
        '/events/random/social/street_performance.json',
        '/events/random/food/coffee_break.json',
        '/events/random/help/find_money.json',
        '/events/random/special/shortcut.json',
        '/events/random/special/lost_way.json',
        // 基础事件链
        '/events/chains/phone_chain.json',
        '/events/chains/leaves/continue_without_phone.json',
        // 特殊事件
        '/events/special/time_travel.json'
      ];
  }

  /**
   * 加载事件数据
   */
  async loadEvents(): Promise<void> {
    try {
      // 首先尝试从索引文件加载事件路径
      const eventFilePaths = await this.loadEventIndex();
      console.log(`Loading ${eventFilePaths.length} events from event index file`);

      const eventPromises = eventFilePaths.map(path => fetch(path));
      const eventResponses = await Promise.allSettled(eventPromises);
      const randomEvents: Event[] = [];
      const chainEvents: Set<string> = new Set();
      const allEvents: Event[] = [];

      for (const response of eventResponses) {
        if (response.status === 'fulfilled' && response.value.ok) {
          // 获取响应文本并解码
          const responseText = await response.value.text();
          const decodedText = decodeContent(responseText);
          const eventData = JSON.parse(decodedText);
          // Check if eventData is an array (e.g., a file containing multiple events directly)
          // This check might be too simplistic if chain files can also be arrays.
          // For now, assuming chain files are objects with an 'events' array property.
          if (Array.isArray(eventData)) {
            // Case 1: The JSON file directly contains an array of events (e.g. a collection of simple random events)
            eventData.forEach((evt: Event) => {
              if (!chainEvents.has(evt.id)) { // Avoid double-adding if also part of a chain file
                allEvents.push(evt);
                if (evt.weight !== 0) { // Only add to random pool if weight is not 0
                    randomEvents.push(evt);
                }
              }
            });
          } else if (eventData.events && Array.isArray(eventData.events)) {
            // Case 2: The JSON file is a chain definition (an object with an 'events' array property)
            eventData.events.forEach((e: Event) => {
              chainEvents.add(e.id);
              allEvents.push(e);
            });
            // Only add the chain's root event to randomEvents if it has a weight > 0
            if (eventData.events[0] && (eventData.events[0].weight === undefined || eventData.events[0].weight > 0)) {
              randomEvents.push(eventData.events[0]);
            }
          } else if (eventData.id) {
            // Case 3: The JSON file defines a single event object (e.g. a leaf node of a chain, or a standalone event not in an array)
            if (!chainEvents.has(eventData.id)) { // Avoid double-adding if already processed via a chain
                allEvents.push(eventData as Event);
                // Only add to random pool if its weight is not explicitly 0
                // and it's not a known chain event (which should only be added via chain root)
                if ((eventData.weight === undefined || eventData.weight > 0) && !isPotentiallyChainInternalEvent(eventData.id, eventFilePaths, chainEvents)) {
                    randomEvents.push(eventData as Event);
                }
            }
          }
        }
      }

      this.randomEvents = randomEvents;
      this.allEvents = allEvents;

      // 构建事件 Map
      this.eventMap.clear();
      for (const event of this.allEvents) {
        this.eventMap.set(event.id, event);
      }
      
      console.log(`Successfully loaded ${allEvents.length} total events, ${randomEvents.length} random events from event index`);
    } catch (error) {
      console.error('Failed to load events from index, falling back to default events:', error);
      this.loadDefaultEvents();
    }
  }

  /**
   * 加载默认事件（当无法从服务器加载事件时使用）
   */
  private loadDefaultEvents(): void {
    const defaultEventDefinitions = {
      random: [
        {
          id: 'traffic_jam',
          title: '交通拥堵',
          description: '前面的路段因为车辆太多而堵塞了。你被困在这里，动弹不得。',
          choices: [
            { id: 'wait', text: '耐心等待', timeChange: 15, distanceChange: 0 },
            { id: 'alternate_route', text: '尝试绕路', timeChange: 10, distanceChange: 2 },
            { id: 'horn', text: '按喇叭表达不满', timeChange: 15, distanceChange: 0 }
          ]
        },
        {
          id: 'rainy_day',
          title: '下雨天',
          description: '天空突然下起了大雨，你被淋得措手不及。',
          choices: [
            { id: 'find_shelter', text: '找个地方避雨', timeChange: 10, distanceChange: 0 },
            { id: 'run_through', text: '冒雨前进', timeChange: 5, distanceChange: 1 },
            { id: 'buy_umbrella', text: '买把伞', timeChange: 8, distanceChange: 0.5 }
          ]
        },
        {
          id: 'lost_way',
          title: '迷路了',
          description: '你发现自己走错了路，需要重新规划路线。',
          choices: [
            { id: 'check_map', text: '查看地图', timeChange: 5, distanceChange: 0 },
            { id: 'ask_direction', text: '问路', timeChange: 8, distanceChange: 0 },
            { id: 'explore', text: '探索新路线', timeChange: 12, distanceChange: 2 }
          ]
        },
        {
          id: 'street_performance',
          title: '街头表演',
          description: '路边有人在表演，吸引了不少人驻足观看。',
          choices: [
            { id: 'watch', text: '驻足观看', timeChange: 10, distanceChange: 0 },
            { id: 'pass_by', text: '直接路过', timeChange: 0, distanceChange: 0.5 },
            { id: 'join', text: '参与表演', timeChange: 20, distanceChange: 0 }
          ]
        },
        {
          id: 'coffee_break',
          title: '咖啡时间',
          description: '路过一家咖啡店，香气四溢。',
          choices: [
            { id: 'buy_coffee', text: '买杯咖啡', timeChange: 8, distanceChange: 0 },
            { id: 'skip', text: '继续赶路', timeChange: 0, distanceChange: 0.5 },
            { id: 'sit_down', text: '坐下来休息', timeChange: 15, distanceChange: 0 }
          ]
        },
        {
          id: 'find_money',
          title: '捡到钱',
          description: '你在地上发现了一些钱。',
          choices: [
            { id: 'pick_up', text: '捡起来', timeChange: 2, distanceChange: 0 },
            { id: 'leave', text: '不理会', timeChange: 0, distanceChange: 0.3 },
            { id: 'find_owner', text: '寻找失主', timeChange: 10, distanceChange: 0 }
          ]
        },
        {
          id: 'shortcut',
          title: '发现捷径',
          description: '你发现了一条可能更快的路线。',
          choices: [
            { id: 'take_shortcut', text: '走捷径', timeChange: -5, distanceChange: 1 },
            { id: 'stay_course', text: '保持原路线', timeChange: 0, distanceChange: 0.5 },
            { id: 'explore', text: '探索新路线', timeChange: 8, distanceChange: 2 }
          ]
        },
        {
          id: 'traffic_control',
          title: '交通管制',
          description: '由于有大领导访问，前方路段实施交通管制，所有车辆都需要绕行。',
          choices: [
            { id: 'detour', text: '按照指示绕行', timeChange: 20, distanceChange: 3 },
            { id: 'wait', text: '等待管制结束', timeChange: 30, distanceChange: 0 },
            { id: 'find_shortcut_tc', text: '寻找小路绕行', timeChange: 15, distanceChange: 4 } 
          ]
        },
        {
          id: 'bus_fight',
          title: '公交冲突',
          description: '公交车上有人因为一点小事发生了争执，场面一度很混乱。',
          choices: [
            { id: 'mediate', text: '上前调解', timeChange: 10, distanceChange: 0, specialEffect: { type: 15, description: "你成功调解了冲突，获得了周围乘客的赞赏。" } },
            { id: 'change_bus', text: '换乘其他车辆', timeChange: 15, distanceChange: 1 },
            { id: 'ignore_bus_fight', text: '保持沉默', timeChange: 5, distanceChange: 0 } 
          ]
        },
        {
          id: 'phone_low_battery',
          title: '手机没电',
          description: '你的手机电量突然告急，可能会影响导航和通讯。',
          choices: [
            { id: 'find_charger', text: '寻找充电宝', timeChange: 10, distanceChange: 0.5 },
            { id: 'power_save', text: '关机省电', timeChange: 5, distanceChange: 0 },
            { id: 'borrow_phone_low_battery', text: '借路人手机', timeChange: 8, distanceChange: 0 } 
          ]
        },
        {
          id: 'meet_celebrity',
          title: '遇到明星',
          description: '路上遇到一个明星，周围有很多粉丝在拍照。',
          choices: [
            { id: 'take_photo', text: '拍照留念', timeChange: 15, distanceChange: 0 },
            { id: 'get_signature', text: '要签名', timeChange: 10, distanceChange: 0 },
            { id: 'leave_quietly', text: '默默离开', timeChange: 2, distanceChange: 0.5 }
          ]
        },
        {
          id: 'meet_reporter',
          title: '遇到记者采访',
          description: '有记者在街头进行随机采访，他们似乎对路人的意见很感兴趣。',
          choices: [
            { id: 'accept_interview', text: '接受采访', timeChange: 15, distanceChange: 0, specialEffect: { type: 15, description: "你接受了采访，获得了曝光机会。" } },
            { id: 'politely_decline', text: '礼貌拒绝', timeChange: 3, distanceChange: 0 },
            { id: 'quick_pass', text: '快速通过', timeChange: 2, distanceChange: 0.5 }
          ]
        }
      ],
      chains: {
        forgot_something_chain: {
          id: 'forgot_something_chain_root',
          title: '忘带东西',
          description: '你突然想起来忘记带重要的工作文件了。',
          choices: [
            { id: 'go_back_fs', text: '返回家里拿', timeChange: 20, distanceChange: -5 }, 
            { id: 'ignore_fs', text: '继续上班，想办法补救', timeChange: 0, distanceChange: 0 },
            { id: 'call_home_fs', text: '打电话给家人，让他们送过来', timeChange: 5, distanceChange: 0 }
          ]
        },
        phone_malfunction_chain: {
          id: 'phone_malfunction_1', 
          title: '手机故障',
          description: '你的手机突然死机了，屏幕完全黑了下来。',
          choices: [
            { id: 'restart_pm', text: '尝试重启手机', timeChange: 5, distanceChange: 0, nextEventId: 'phone_malfunction_2' },
            { id: 'continue_use_pm', text: '继续使用', timeChange: 0, distanceChange: 0.5, nextEventId: 'phone_malfunction_3' },
            { id: 'find_repair_pm', text: '找维修店', timeChange: 15, distanceChange: 1, nextEventId: 'phone_repair_shop' }
          ],
          linkedEvents: [
            {
              id: 'phone_malfunction_2',
              title: '手机异常',
              description: '重启后手机出现异常，系统运行缓慢，应用频繁崩溃。',
              choices: [
                { id: 'factory_reset_pm2', text: '恢复出厂设置', timeChange: 20, distanceChange: 0, nextEventId: 'phone_factory_reset' },
                { id: 'try_other_pm2', text: '尝试其他方法', timeChange: 10, distanceChange: 0, nextEventId: 'phone_malfunction_3' },
                { id: 'give_up_pm2', text: '放弃修复', timeChange: 5, distanceChange: 0.5, nextEventId: 'phone_give_up' }
              ]
            },
            {
              id: 'phone_malfunction_3',
              title: '手机完全故障',
              description: '手机完全无法使用，所有功能都失效了。',
              choices: [
                { id: 'buy_new_pm3', text: '买新手机', timeChange: 30, distanceChange: 2, nextEventId: 'buy_new_phone' },
                { id: 'continue_pm3', text: '继续赶路', timeChange: 5, distanceChange: 1, nextEventId: 'continue_without_phone' },
                { id: 'borrow_pm3', text: '借路人手机', timeChange: 8, distanceChange: 0, nextEventId: 'borrow_phone' }
              ]
            }
          ]
        },
        help_elderly_chain: {
          id: 'help_elderly_1', 
          title: '遇到需要帮助的老人',
          description: '一位老人似乎遇到了困难，需要帮助。',
          choices: [
            { id: 'help_he1', text: '主动帮助', timeChange: 5, distanceChange: 0, nextEventId: 'help_elderly_2' },
            { id: 'ask_he1', text: '询问情况', timeChange: 3, distanceChange: 0, nextEventId: 'help_elderly_3' },
            { id: 'seek_help_he1', text: '寻求他人帮助', timeChange: 5, distanceChange: 0, nextEventId: 'help_elderly_4' }
          ],
          linkedEvents: [
            {
              id: 'help_elderly_2',
              title: '老人需要送医',
              description: '老人突然感到不适，需要立即送医院。',
              choices: [
                { id: 'ambulance_he2', text: '叫救护车', timeChange: 15, distanceChange: 0, nextEventId: 'call_ambulance', specialEffect: { type: 15, description: "你及时叫来了救护车，老人得到了及时救治。" } },
                { id: 'taxi_he2', text: '打车送医', timeChange: 20, distanceChange: 3, nextEventId: 'take_taxi_hospital' },
                { id: 'family_he2', text: '联系家人', timeChange: 10, distanceChange: 0, nextEventId: 'contact_family' }
              ]
            },
            {
              id: 'help_elderly_3',
              title: '老人迷路',
              description: '老人只是迷路了，需要帮助找到回家的路。',
              choices: [
                { id: 'send_home_he3', text: '送回家', timeChange: 25, distanceChange: 4, nextEventId: 'send_home', specialEffect: { type: 15, description: "你帮助老人安全回到家，获得了家人的感谢。" } },
                { id: 'directions_he3', text: '帮忙指路', timeChange: 10, distanceChange: 0, nextEventId: 'give_directions' },
                { id: 'contact_he3', text: '联系家人', timeChange: 8, distanceChange: 0, nextEventId: 'contact_family' }
              ]
            },
            {
              id: 'help_elderly_4',
              title: '等待帮助',
              description: '你在等待其他人来帮助老人。',
              choices: [
                { id: 'wait_he4', text: '继续等待', timeChange: 15, distanceChange: 0, nextEventId: 'wait_for_help' },
                { id: 'police_he4', text: '报警求助', timeChange: 10, distanceChange: 0, nextEventId: 'call_police' },
                { id: 'leave_he4', text: '继续赶路', timeChange: 5, distanceChange: 0.5, nextEventId: 'continue_journey' }
              ]
            }
          ]
        },
        food_festival_chain: {
          id: 'food_festival_1', 
          title: '遇到美食节',
          description: '路边正在举办美食节，各种美食香气四溢，让人垂涎欲滴。',
          choices: [
            { id: 'taste_ff1', text: '品尝美食', timeChange: 10, distanceChange: 0, nextEventId: 'food_festival_2' },
            { id: 'buy_ff1', text: '买些带走', timeChange: 5, distanceChange: 0, nextEventId: 'food_festival_3' },
            { id: 'continue_ff1', text: '继续赶路', timeChange: 2, distanceChange: 0.5, nextEventId: 'continue_journey' }
          ],
          linkedEvents: [
            {
              id: 'food_festival_2',
              title: '遇到美食博主',
              description: '在品尝美食时，你遇到了一位正在直播的美食博主。',
              choices: [
                { id: 'interview_ff2', text: '接受采访', timeChange: 15, distanceChange: 0, nextEventId: 'food_blogger_interview', specialEffect: { type: 15, description: "你接受了美食博主的采访，获得了曝光机会。" } },
                { id: 'share_ff2', text: '分享美食', timeChange: 10, distanceChange: 0, nextEventId: 'share_food' },
                { id: 'taste_more_ff2', text: '继续品尝', timeChange: 20, distanceChange: 0, nextEventId: 'continue_food_tasting' }
              ]
            },
            {
              id: 'food_festival_3',
              title: '美食优惠活动',
              description: '你发现美食节正在举办优惠活动，购买美食可以参与抽奖。',
              choices: [
                { id: 'join_activity_ff3', text: '参加活动', timeChange: 15, distanceChange: 0, nextEventId: 'food_festival_activity' },
                { id: 'buy_directly_ff3', text: '直接购买', timeChange: 8, distanceChange: 0.5, nextEventId: 'buy_food_directly' },
                { id: 'give_up_ff3', text: '放弃购买', timeChange: 3, distanceChange: 0.3, nextEventId: 'give_up_buying' }
              ]
            }
          ]
        }
      }
    };

    const defaultRandomEvents: Event[] = [...defaultEventDefinitions.random];
    const defaultAllEvents: Event[] = [...defaultEventDefinitions.random];

    for (const chainName in defaultEventDefinitions.chains) {
      if (Object.prototype.hasOwnProperty.call(defaultEventDefinitions.chains, chainName)) {
        const chain = (defaultEventDefinitions.chains as any)[chainName]; 
        if (chain && typeof chain === 'object' && chain.id && chain.choices) { // It's the root of a chain
          defaultRandomEvents.push(chain as Event); // Add root to random events
          defaultAllEvents.push(chain as Event);    // Add root to all events
          if (chain.linkedEvents && Array.isArray(chain.linkedEvents)) {
            chain.linkedEvents.forEach((linkedEvent: Event) => {
              defaultAllEvents.push(linkedEvent); // Add linked events to all events
            });
          }
        }
      }
    }

    this.randomEvents = defaultRandomEvents;
    this.allEvents = defaultAllEvents;
    
    this.eventMap.clear();
    for (const event of this.allEvents) {
      this.eventMap.set(event.id, event);
    }
  }

  /**
   * 获取随机事件
   * @param state 当前游戏状态
   * @returns 随机事件或null
   */
  getRandomEvent(state: GameState): Event | null {
    const seenEvents = this.stateManager.getSeenEvents();
    let potentialEvents = this.randomEvents.filter(event => 
      event.id !== 'nothing_happened' && !seenEvents.has(event.id)
    );

    if (potentialEvents.length === 0 && this.randomEvents.some(event => event.id !== 'nothing_happened')) {
      potentialEvents = this.randomEvents.filter(event => event.id !== 'nothing_happened');
    }
    
    let availableEvents = potentialEvents.filter(event => {
      const minDistance = event.minDistance === undefined ? 0 : event.minDistance;
      const maxDistance = event.maxDistance === undefined ? Infinity : event.maxDistance;
      return state.distanceToWork >= minDistance && state.distanceToWork <= maxDistance;
    });

    availableEvents = availableEvents.filter(event => {
      return this.stateManager.checkEventConditions(event.conditions || []);
    });

    if (availableEvents.length > 0) {
      const selectedEvent = this.getWeightedRandomEvent(availableEvents);
      if (selectedEvent) {
        this.stateManager.addSeenEvent(selectedEvent.id);
        return selectedEvent;
      }
    }

    const nothingHappenedEvent = this.eventMap.get('nothing_happened');
    if (nothingHappenedEvent) {
      return nothingHappenedEvent;
    }
    
    return null;
  }

  /**
   * 根据权重选择随机事件
   * @param events 事件列表
   * @param state 当前游戏状态
   * @returns 选中的事件或null
   */
  private getWeightedRandomEvent(events: Event[]): Event | null {
    if (events.length === 0) {
      return null;
    }

    const totalWeight = events.reduce((sum, event) => sum + (event.weight || 1), 0);
    if (totalWeight === 0) { // 如果所有事件权重都是0，则均等选择
        const randomIndex = Math.floor(Math.random() * events.length);
        return events[randomIndex];
    }

    let randomNum = Math.random() * totalWeight;
    for (const event of events) {
      const weight = event.weight || 1;
      if (randomNum < weight) {
        return event;
      }
      randomNum -= weight;
    }
    return null; // Should not happen if totalWeight > 0
  }

  /**
   * 通过ID获取事件
   * @param id 事件ID
   * @returns 事件或null
   */
  getEventById(id: string): Event | null {
    const event = this.eventMap.get(id);
    if (!event) {
      return null;
    }
    return event;
  }

  /**
   * 获取所有事件
   * @returns 所有事件
   */
  getAllEvents(): Event[] {
    return [...this.allEvents];
  }
} // End of GameEventSystem class

// Helper function (should be outside the class or static if preferred and language features allow)
function isPotentiallyChainInternalEvent(eventId: string, eventFilePaths: string[], chainEvents: Set<string>): boolean {
    // A simple heuristic: if the eventId is in chainEvents set, it's part of a chain.
    // This doesn't prevent it from being a root of another chain if misconfigured,
    // but helps avoid adding non-root chain events to randomEvents directly.
    // Also check if the eventID is a part of a known chain file path, if not already in chainEvents from parsing chain files.
    if (chainEvents.has(eventId)) {
        return true;
    }
    for (const path of eventFilePaths) {
        if (path.includes('/chains/') && path.includes(eventId)) {
            // This is a simplistic check and might need refinement
            // It assumes eventId might be part of the filename for chain-specific events
            return true;
        }
    }
    return false;
}
